import { addDays, addHours } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';

import { ONE_HOUR } from '../../scenes/Scheduling/config';
import { getZonedDateTimezoneOffset, getZonedOrLocalTimezoneOffset } from '../../utils/date';

export type TimezoneDSTChangeWithinRange = {
  date: Date;
  time: number;
} | null;

export const findTimezoneDSTChangeWithinRange = (
  startDate: Date,
  endDate: Date,
  timezone: string,
  localeObj?: Locale
): TimezoneDSTChangeWithinRange => {
  const zonedStartDate = utcToZonedTime(startDate.getTime(), timezone);
  const zonedEndDate = utcToZonedTime(endDate.getTime(), timezone);
  const startDateTimezoneOffset = getZonedOrLocalTimezoneOffset(zonedStartDate, timezone);
  const endDateTimezoneOffset = getZonedOrLocalTimezoneOffset(zonedEndDate, timezone);

  const DSTTimezoneChangeOffset = startDateTimezoneOffset - endDateTimezoneOffset;
  const isRangeContainingDSTTimezoneChange = DSTTimezoneChangeOffset !== 0;

  if (!isRangeContainingDSTTimezoneChange) {
    return null;
  }

  let timezoneDSTChangeDay = null;
  let tempTimezoneDSTChangeDay = startDate;
  let hourBeforeDSTChange = null;

  while (!timezoneDSTChangeDay) {
    const nextDay = addDays(tempTimezoneDSTChangeDay, 1);
    const nextDayZoned = utcToZonedTime(nextDay.getTime(), timezone);

    if (getZonedDateTimezoneOffset(nextDayZoned, timezone) !== startDateTimezoneOffset) {
      timezoneDSTChangeDay = tempTimezoneDSTChangeDay;
    }

    tempTimezoneDSTChangeDay = nextDay;
  }

  let tempTimezoneDSTChangeHour = timezoneDSTChangeDay;

  while (!hourBeforeDSTChange) {
    const nextHour = addHours(tempTimezoneDSTChangeHour, 1);
    const nextHourZoned = utcToZonedTime(nextHour, timezone);

    if (getZonedOrLocalTimezoneOffset(nextHourZoned, timezone) !== startDateTimezoneOffset) {
      hourBeforeDSTChange = tempTimezoneDSTChangeHour;
    }

    tempTimezoneDSTChangeHour = nextHour;
  }

  // add 1 hour only when winter time changes to summer time
  const extraHourModification = Number(DSTTimezoneChangeOffset > 0);

  return {
    date: tempTimezoneDSTChangeHour, // do note that time value within date is not relevant
    time:
      (Number(format(utcToZonedTime(hourBeforeDSTChange, timezone), 'H', { locale: localeObj, timeZone: timezone })) +
        extraHourModification) *
      ONE_HOUR,
  };
};
